import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { queryGoogleCalendar } from "../api/actions/query";
import { cancelEvent, retryBookingAPI, resendInvoiceAPI } from "../api/actions/mutation";
import { getAppointmentMetrics } from "../../user/api/actions/query";
import { store } from "../../../setup/store";
import EventDetailsPopup from "./EventDetailsPopup";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Skeleton,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Chip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

// Helper function to format date/time in the event's timezone
const formatEventDateTime = (dateTimeString, timeZone) => {
  if (!dateTimeString) return { dateStr: 'N/A', timeStr: '' };
  const date = new Date(dateTimeString);
  const options = timeZone ? { timeZone } : {};

  // Format date
  const dateStr = date.toLocaleDateString('en-US', {
    ...options,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  // Format time
  const timeStr = date.toLocaleTimeString('en-US', {
    ...options,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return { dateStr, timeStr };
};


const List = ({ canDelete }) => {
  const [events, setEvents] = useState([]);
  const [filterMode, setFilterMode] = useState("all_details");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [eventToCancel, setEventToCancel] = useState(null);
  const eventsPerPage = 10;

  const [metricsEvents, setMetricsEvents] = useState([]);
  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  const [providerFilter, setProviderFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState({ id: null, type: null }); // { id: eventId, type: 'invite' | 'invoice' }

  // New Filters & Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [meetingStatusFilter, setMeetingStatusFilter] = useState("all");
  const [eventTimingFilter, setEventTimingFilter] = useState("all"); // 'all' | 'upcoming' | 'past'
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all"); // 'all' | 'sent' | 'not_sent'

  const settings = useSelector(state => state.settings?.settings);
  const isGoogleEnabled = settings?.isCalendarGoogleEnabled === true;
  const isOutlookEnabled = settings?.isCalendarOutlookEnabled === true;
  const showProviderFilter = isGoogleEnabled && isOutlookEnabled;

  const fetchEvents = async () => {
    try {
      const fetchedEvents = await queryGoogleCalendar();
      if (fetchedEvents.data) setEvents(fetchedEvents.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsEvents = async () => {
    setIsMetricsLoading(true);
    try {
      const response = await store.dispatch(getAppointmentMetrics());
      if (response && response.data && response.data.success) {
        const appointments = response.data.data.appointments || [];
        // Sort by creation date - newest first
        const sortedAppointments = appointments.sort((a, b) =>
          new Date(b.created || b.createdAt) - new Date(a.created || a.createdAt)
        );
        setMetricsEvents(sortedAppointments);
      }
    } catch (error) {
      console.error("Error fetching metrics events:", error);
    } finally {
      setIsMetricsLoading(false);
    }
  };

  useEffect(() => {
    if (filterMode === 'all_details' && metricsEvents.length === 0) {
      fetchMetricsEvents();
    }
  }, [filterMode]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const filteredEvents = filterMode === "app"
    ? events.filter((event) => event.conferenceData?.createRequest?.requestId?.startsWith("Datum"))
    : filterMode === "all" ? events : [];

  // Pagination logic needs to handle both event sources
  let currentList = filterMode === 'all_details' ? metricsEvents : filteredEvents;

  // 1. Provider Filter
  if (filterMode === 'all_details' && showProviderFilter && providerFilter !== 'all') {
    currentList = currentList.filter(event => (event.calendarProvider || 'google') === providerFilter);
  }

  // 2. Search Query Filter (Contact Name, Place, Email, Phone, Ref ID)
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    currentList = currentList.filter(event => {
      const name = (event.contactName || event.summary || '').toLowerCase();
      const place = (event.place || '').toLowerCase();
      const email = (event.email || event.customerEmail || '').toLowerCase();
      const phone = (event.phone || event.customerPhone || '').toLowerCase();
      const refId = (event.appointmentRefId || event.refId || '').toLowerCase();
      return name.includes(q) || place.includes(q) || email.includes(q) || phone.includes(q) || refId.includes(q);
    });
  }

  // 3. Event Timing Filter (Upcoming vs Past) using Configured Business Timezone
  if (eventTimingFilter !== 'all') {
    const tz = settings?.timeZone || 'UTC';
    const nowWallClock = new Intl.DateTimeFormat('sv-SE', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).format(new Date()).replace(' ', 'T');

    currentList = currentList.filter(event => {
      const dateStr = event.scheduledDate || event.scheduledEndDate || event.start?.dateTime;
      if (!dateStr) return true;

      let apptWallClock = dateStr;
      if (dateStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
        apptWallClock = new Intl.DateTimeFormat('sv-SE', {
          timeZone: tz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false
        }).format(new Date(dateStr)).replace(' ', 'T');
      }

      if (eventTimingFilter === 'upcoming') {
        return apptWallClock >= nowWallClock;
      } else if (eventTimingFilter === 'past') {
        return apptWallClock < nowWallClock;
      }
      return true;
    });
  }

  // 4. Payment Status Filter
  if (paymentStatusFilter !== 'all') {
    currentList = currentList.filter(event => {
      const status = (event.paymentStatus || '').toLowerCase();
      const mode = (event.paymentMode || '').toLowerCase();
      if (paymentStatusFilter === 'free') {
        return mode === 'unpaid' || status === 'not_required';
      }
      return status === paymentStatusFilter;
    });
  }

  // 5. Meeting Status Filter
  if (meetingStatusFilter !== 'all') {
    currentList = currentList.filter(event => {
      const apptStatus = (event.appointmentStatus || '').toLowerCase();
      return apptStatus === meetingStatusFilter;
    });
  }

  // 6. Invoice Status Filter (Sent vs Not Sent)
  if (invoiceStatusFilter !== 'all') {
    currentList = currentList.filter(event => {
      // 1. Explicit success flags
      const isExplicitlySent = event.invoiceSent === true || event.isInvoiceSent === true || event.invoiceStatus === 'sent' || event.invoiceStatus === 'completed';
      
      // 2. Failure or draft flags (e.g. SMTP email failed, PDF error, or draft status)
      const isFailedOrUnsent = event.invoiceSent === false || event.isInvoiceSent === false || event.invoiceStatus === 'failed' || event.invoiceStatus === 'draft' || !!event.invoiceError;
      
      // 3. Generated invoice reference presence
      const hasInvoiceRef = !!(event.invoiceNumber || event.invoiceId || event.invoiceDetails?.invoiceNumber);
      
      // Invoice is SENT only if explicitly sent OR has invoice ref without any email delivery failure flags
      const isSent = isExplicitlySent || (hasInvoiceRef && !isFailedOrUnsent);

      if (invoiceStatusFilter === 'sent') return isSent;
      if (invoiceStatusFilter === 'not_sent') return !isSent;
      return true;
    });
  }

  const isLoading = filterMode === 'all_details' ? isMetricsLoading : loading;

  const handleEventClick = (event) => {
    // Only for standard events for now, or adapt for metrics events
    if (filterMode !== 'all_details') {
      setSelectedEvent(event);
    }
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const handleCancelEvent = (event) => {
    setEventToCancel(event);
    setOpenConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    try {
      const eventId = eventToCancel?.id || eventToCancel?.appointmentId || (typeof eventToCancel === 'string' ? eventToCancel : null);
      const crmId = eventToCancel?.crmId;
      const appointmentRefId = eventToCancel?.appointmentRefId;

      await cancelEvent({ eventId, crmId, appointmentRefId });
      if (filterMode === 'all_details') {
        await fetchMetricsEvents();
      } else {
        await fetchEvents();
      }
    } catch (error) {
      console.error("Error canceling event:", error);
    } finally {
      setOpenConfirmDialog(false);
      setEventToCancel(null);
    }
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setEventToCancel(null);
  };

  const getCrmAppointmentForEvent = (event) => {
    return metricsEvents.find(c => c.appointmentId === event.id);
  };

  const handleResendInvite = async (event) => {
    // If event has crmId, it's already a CRM appointment from metrics
    const crmAppt = (event.crmId && event.appointmentRefId) ? event : getCrmAppointmentForEvent(event);

    if (!crmAppt) {
      alert("No CRM appointment found. Cannot resend invite.");
      return;
    }

    // ✅ BLOCK: Don't allow resending invite for paid appointments if payment is not completed
    if (crmAppt.paymentMode?.toLowerCase() === 'paid' && crmAppt.paymentStatus?.toLowerCase() !== 'completed') {
      alert("Please complete the payment first before resending the invite.");
      return;
    }

    // ✅ BLOCK: Don't allow resending invite for past appointments
    const appointmentDate = crmAppt.scheduledEndDate || crmAppt.scheduledDate || event?.start?.dateTime;
    if (appointmentDate) {
      const tz = settings?.timeZone || event.start?.timeZone || 'UTC';

      // Get current "wall-clock" time in the business timezone
      const nowWallClock = new Intl.DateTimeFormat('sv-SE', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      }).format(new Date()).replace(' ', 'T');

      // Normalize appointment time to wall-clock string in the same timezone for comparison
      let apptWallClock = appointmentDate;
      // If it's a full ISO string (contains Z or offset), format it to wall-clock in target TZ
      if (appointmentDate.includes('Z') || /[+-]\d{2}:\d{2}$/.test(appointmentDate)) {
        apptWallClock = new Intl.DateTimeFormat('sv-SE', {
          timeZone: tz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false
        }).format(new Date(appointmentDate)).replace(' ', 'T');
      }

      if (apptWallClock < nowWallClock) {
        alert(`This appointment time has already passed (${apptWallClock}). Cannot resend invite.`);
        return;
      }
    }

    try {
      setActionLoading({ id: event.id || event.appointmentId, type: 'invite' });

      await retryBookingAPI({
        crmId: crmAppt.crmId,
        appointmentRefId: crmAppt.appointmentRefId,
        isResendInvite: true,
        eventDetails: {
          region: crmAppt.region,
          purpose: crmAppt.message || crmAppt.purpose || crmAppt.summary
        },
        paymentDetails: crmAppt.paymentDetails,
        notifyByEmail: true
      });

      alert("Invite resend triggered successfully.");
      fetchMetricsEvents();
      fetchEvents();
    } catch (err) {
      console.error("Resend invite error:", err);
      const errMsg = err.message || "";
      if (errMsg.toLowerCase().includes("booked") || errMsg.toLowerCase().includes("slot")) {
        alert("Schedule time already booked. Change time and resend invite.");
      } else {
        alert("Failed to resend invite: " + errMsg);
      }
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handleResendInvoice = async (event) => {
    const crmAppt = (event.crmId && event.appointmentRefId) ? event : getCrmAppointmentForEvent(event);

    if (!crmAppt) {
      alert("No CRM appointment found. Cannot resend invoice.");
      return;
    }

    if (!window.confirm("Would you like to resend this invoice to the customer?")) return;

    try {
      setActionLoading({ id: event.id || event.appointmentId, type: 'invoice' });
      const result = await resendInvoiceAPI({
        crmId: crmAppt.crmId,
        appointmentRefId: crmAppt.appointmentRefId
      });

      alert(result.message || "Invoice resent successfully");

      // Refresh CRM metrics to show updated invoice status
      fetchMetricsEvents();
    } catch (err) {
      console.error("Resend invoice error:", err);
      // Check if it's the specific "not found" error, support Axios/Fetch formats
      const errMsg = err.message || "";
      if (errMsg.includes("not found") || errMsg.includes("deleted")) {
          if (window.confirm("Invoice not found or has been deleted. Can I create a new one and send it?")) {
              // Call API again with forceCreate: true
              try {
                  setActionLoading({ id: event.id || event.appointmentId, type: 'invoice' });
                  const createResult = await resendInvoiceAPI({
                      crmId: crmAppt.crmId,
                      appointmentRefId: crmAppt.appointmentRefId,
                      forceCreate: true
                  });
                  alert(createResult.message || "New invoice created and sent successfully");
                  fetchMetricsEvents();
              } catch (createErr) {
                  alert("Failed to create and send invoice: " + (createErr.message || ""));
              } finally {
                  setActionLoading({ id: null, type: null });
              }
              return;
          }
      } else {
          alert("Failed to resend invoice: " + errMsg);
      }
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const paginatedEvents = currentList.slice(
    (currentPage - 1) * eventsPerPage,
    currentPage * eventsPerPage
  );
  const getEventById = (id) => {
    return events.find(e => e.id === id);
  };

  const handleOpenCalendar = () => {
    const event = getEventById(eventToCancel);
    if (event?.htmlLink) {
      window.open(event.htmlLink, "_blank", "noopener,noreferrer");
    }
  };


  return (
    <Box sx={{ mb: -3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ mb: 3 }}>
          Events
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Select
            value={filterMode}
            onChange={(e) => {
              setFilterMode(e.target.value);
              setCurrentPage(1);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="app">Events Created from Web</MenuItem>
            <MenuItem value="all">All Events</MenuItem>
            <MenuItem value="all_details">All Events Details</MenuItem>
          </Select>
          {filterMode === "all_details" && showProviderFilter && (
            <Select
              value={providerFilter}
              onChange={(e) => {
                setProviderFilter(e.target.value);
                setCurrentPage(1);
              }}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Providers</MenuItem>
              <MenuItem value="google">Google</MenuItem>
              <MenuItem value="outlook">Outlook</MenuItem>
            </Select>
          )}
        </Box>
      </Box>

      {/* Search and Filters Bar */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Grid container spacing={1.5} alignItems="center">
          {/* Search Box */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search contact, place, ref ID..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchQuery(''); setCurrentPage(1); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>

          {/* Event Timing Filter (Upcoming vs Past) */}
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel id="event-timing-label">Event Timing</InputLabel>
              <Select
                labelId="event-timing-label"
                label="Event Timing"
                value={eventTimingFilter}
                onChange={(e) => { setEventTimingFilter(e.target.value); setCurrentPage(1); }}
              >
                <MenuItem value="all">All Events</MenuItem>
                <MenuItem value="upcoming">Upcoming Events</MenuItem>
                <MenuItem value="past">Past Events</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel id="payment-status-label">Payment Status</InputLabel>
              <Select
                labelId="payment-status-label"
                label="Payment Status"
                value={paymentStatusFilter}
                onChange={(e) => { setPaymentStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <MenuItem value="all">All Payment Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="initiated">Initiated</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="free">Free Booking</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Meeting Status Filter */}
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel id="meeting-status-label">Meeting Status</InputLabel>
              <Select
                labelId="meeting-status-label"
                label="Meeting Status"
                value={meetingStatusFilter}
                onChange={(e) => { setMeetingStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <MenuItem value="all">All Meeting Statuses</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="payment_completed">Payment Completed</MenuItem>
                <MenuItem value="payment_initiated">Payment Initiated</MenuItem>
                <MenuItem value="user_saved">User Saved</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Invoice Status Filter */}
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel id="invoice-status-label">Invoice Status</InputLabel>
              <Select
                labelId="invoice-status-label"
                label="Invoice Status"
                value={invoiceStatusFilter}
                onChange={(e) => { setInvoiceStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <MenuItem value="all">All Invoices</MenuItem>
                <MenuItem value="sent">Invoice Sent</MenuItem>
                <MenuItem value="not_sent">Invoice Not Sent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[...Array(5)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : filterMode === 'all_details' && paginatedEvents.length > 0 ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contact Name</TableCell>
                <TableCell>Place</TableCell>
                <TableCell>Booked On</TableCell>
                <TableCell>Appointment Date/Time</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Meeting Status</TableCell>
                {showProviderFilter && <TableCell>Provider</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvents.map((event, index) => {
                const timeZone = event.start?.timeZone || 'UTC';

                const createdDate = event.created || event.createdAt;
                const { dateStr: createdDateStr, timeStr: createdTimeStr } = createdDate
                  ? formatEventDateTime(createdDate, timeZone)
                  : { dateStr: 'N/A', timeStr: '' };

                return (
                  <TableRow key={index} hover>
                    <TableCell>{event.contactName || (event.summary ? event.summary.replace('Meeting with ', '') : 'N/A')}</TableCell>
                    <TableCell>{event.place || 'N/A'}</TableCell>
                    <TableCell>
                      {createdDateStr} {createdTimeStr}
                      {timeZone && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, fontWeight: 'bold', color: 'text.secondary' }}
                        >
                          ({timeZone})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const scheduledDate = event.scheduledDate || event.start?.dateTime;
                        if (!scheduledDate) return 'N/A';
                        const { dateStr: schedDateStr, timeStr: schedTimeStr } = formatEventDateTime(scheduledDate);
                        return (
                          <>
                            {schedDateStr} {schedTimeStr}
                            {timeZone && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, fontWeight: 'bold', color: 'text.secondary' }}
                              >
                                ({timeZone})
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        color: event.paymentStatus === 'completed' ? 'green' : 'orange',
                        textTransform: 'capitalize'
                      }}>
                        {event.paymentMode === 'unpaid' && event.paymentStatus === 'not_required'
                          ? 'Free Booking'
                          : (event.paymentStatus || 'N/A')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        color: event.appointmentStatus === 'cancelled' || event.appointmentStatus === 'failed' ? 'red' : 'inherit',
                        textTransform: 'capitalize'
                      }}>
                        {event.appointmentStatus || 'N/A'}
                      </Box>
                    </TableCell>
                    {showProviderFilter && (
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {event.calendarProvider || 'Google'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          disabled={actionLoading.id === (event.id || event.appointmentId) || event.appointmentStatus === 'cancelled'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvite(event);
                          }}
                          sx={{ textTransform: "none" }}
                          startIcon={actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invite' ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invite' ? 'Resending...' : 'Resend Invite'}
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          variant="outlined"
                          disabled={actionLoading.id === (event.id || event.appointmentId) || event.appointmentStatus === 'cancelled'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvoice(event);
                          }}
                          sx={{ textTransform: "none" }}
                          startIcon={actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invoice' ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invoice' ? 'Resending...' : 'Resend Invoice'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : paginatedEvents.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {paginatedEvents.map((event) => {
            // Get the event's timezone
            const timeZone = event.start?.timeZone || 'UTC';

            // Format date and time in the preferred timezone
            const { dateStr, timeStr } = formatEventDateTime(
              event.start.dateTime,
              settings?.timeZone || timeZone
            );

            return (
              <Card
                key={event.id}
                onClick={() => handleEventClick(event)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: "medium" }}
                  >
                    {event.summary || "No Title"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    {dateStr} • {timeStr}
                    {(settings?.timeZone || timeZone) && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, fontWeight: 'bold' }}
                      >
                        ({settings?.timeZone || timeZone})
                      </Typography>
                    )}
                  </Typography>
                  {event.attendees && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 12,
                          bgcolor: "#0d3559",
                        }}
                      >
                        {event.attendees.length}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {event.attendees.length} attendees
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                {(event.conferenceData?.createRequest?.requestId?.startsWith("Datum") || getCrmAppointmentForEvent(event)) && (
                  <CardActions>
                    <Button
                      size="small"
                      color="primary"
                      disabled={actionLoading.id === (event.id || event.appointmentId)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendInvite(event);
                      }}
                      sx={{ textTransform: "none" }}
                      startIcon={actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invite' ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invite' ? 'Resending...' : 'Resend Invite'}
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      disabled={actionLoading.id === (event.id || event.appointmentId)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendInvoice(event);
                      }}
                      sx={{ textTransform: "none" }}
                      startIcon={actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invoice' ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {actionLoading.id === (event.id || event.appointmentId) && actionLoading.type === 'invoice' ? 'Resending...' : 'Resend Invoice'}
                    </Button>
                  </CardActions>
                )}
              </Card>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          No meetings
        </Typography>
      )
      }
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={Math.ceil(currentList.length / eventsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      {
        selectedEvent && (
          <EventDetailsPopup event={selectedEvent} onClose={handleCloseModal} />
        )
      }
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent sx={{ fontSize: 14 }}>
          Are you sure you want to cancel this meeting?
          <br />
          <Typography variant="caption" color="text.secondary">
            We will send a cancellation email to all participants on your behalf.
          </Typography>
        </DialogContent>


        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary" sx={{ textTransform: "none" }}>
            No
          </Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus sx={{ textTransform: "none" }}>
            Yes
          </Button>
          <Button
            onClick={handleOpenCalendar}
            color="error"
            sx={{ textTransform: "none" }}
            disabled={!getEventById(eventToCancel)?.htmlLink}
          >
            Cancel via Google Calendar
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default List;